import React, { type ReactNode } from 'react';

import { type MessageDescriptor } from 'react-intl-next';

import Blog16Icon from '@atlaskit/icon-object/glyph/blog/16';
import Bug16Icon from '@atlaskit/icon-object/glyph/bug/16';
import Epic16Icon from '@atlaskit/icon-object/glyph/epic/16';
import Issue16Icon from '@atlaskit/icon-object/glyph/issue/16';
import PageLiveDoc16Icon from '@atlaskit/icon-object/glyph/page-live-doc/16';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import Story16Icon from '@atlaskit/icon-object/glyph/story/16';
import Task16Icon from '@atlaskit/icon-object/glyph/task/16';
import SmartLinkIcon from '@atlaskit/icon/core/smart-link';
import { type LinkPickerPluginAction, type LinkSearchListItemData } from '@atlaskit/link-picker';
import { LoomIcon } from '@atlaskit/logo';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import DatabaseObject from '@atlaskit/object/database';
import WhiteboardObject from '@atlaskit/object/whiteboard';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { EmptyStateNoResults } from './empty-state-no-results';
import { messages } from './messages';

enum LinkContentType {
	'jira.issue',
	'jira.issue.bug',
	'jira.issue.story',
	'jira.issue.task',
	'jira.issue.epic',
	'jira.dashboard',
	'jira.board',
	'jira.project',
	'jira.filter',
	'confluence.page',
	'confluence.blogpost',
	'confluence.whiteboard',
	'loom.video',
	'confluence.database',
	'confluence.embed',
}

export const renderEmptyStateNoResults = (
	product: string,
	action?: LinkPickerPluginAction,
	cloudId?: string,
	isMultisiteSearch?: boolean,
): ReactNode => {
	return (
		<EmptyStateNoResults
			product={product}
			action={action}
			cloudId={cloudId}
			isMultisiteSearch={isMultisiteSearch}
		/>
	);
};

export const isLinkContentType = (
	contentType?: string,
): contentType is keyof typeof LinkContentType => {
	return Boolean(contentType && Object.keys(LinkContentType).includes(contentType));
};

export const mapContentTypeToIcon: Record<
	keyof typeof LinkContentType,
	{
		icon: React.ComponentType<{ alt: string }>;
		iconAlt: MessageDescriptor;
	}
> = {
	'jira.issue': {
		icon: ({ alt }) => <Issue16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconIssueLabel,
	},
	'jira.issue.bug': {
		icon: ({ alt }) => <Bug16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconBugLabel,
	},
	'jira.issue.story': {
		icon: ({ alt }) => <Story16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconStoryLabel,
	},
	'jira.issue.task': {
		icon: ({ alt }) => <Task16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconTaskLabel,
	},
	'jira.issue.epic': {
		icon: ({ alt }) => <Epic16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconEpicLabel,
	},
	'jira.dashboard': {
		icon: ({ alt }) => (
			<JiraIcon
				label={alt}
				size="xxsmall"
				appearance="brand"
				{...(fg('navx-1895-new-logo-design') ? { shouldUseNewLogoDesign: true } : undefined)}
			/>
		),
		iconAlt: messages.hyperlinkIconDashboardLabel,
	},
	'jira.board': {
		icon: ({ alt }) => (
			<JiraIcon
				label={alt}
				size="xxsmall"
				appearance="brand"
				{...(fg('navx-1895-new-logo-design') ? { shouldUseNewLogoDesign: true } : undefined)}
			/>
		),
		iconAlt: messages.hyperlinkIconBoardLabel,
	},
	'jira.project': {
		icon: ({ alt }) => (
			<JiraIcon
				label={alt}
				size="xxsmall"
				appearance="brand"
				{...(fg('navx-1895-new-logo-design') ? { shouldUseNewLogoDesign: true } : undefined)}
			/>
		),
		iconAlt: messages.hyperlinkIconProjectLabel,
	},
	'jira.filter': {
		icon: ({ alt }) => (
			<JiraIcon
				label={alt}
				size="xxsmall"
				appearance="brand"
				{...(fg('navx-1895-new-logo-design') ? { shouldUseNewLogoDesign: true } : undefined)}
			/>
		),
		iconAlt: messages.hyperlinkIconFilterLabel,
	},
	'confluence.page': {
		icon: ({ alt }) => <Page16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconPageLabel,
	},
	'confluence.blogpost': {
		icon: ({ alt }) => <Blog16Icon label={alt} />,
		iconAlt: messages.hyperlinkIconBlogLabel,
	},
	'confluence.whiteboard': {
		icon: ({ alt }) => <WhiteboardObject label={alt} />,
		iconAlt: messages.hyperlinkIconWhiteboardLabel,
	},
	'loom.video': {
		icon: ({ alt }) => <LoomIcon label={alt} shouldUseNewLogoDesign size="xxsmall" />,
		iconAlt: messages.hyperlinkIconLoomLabel,
	},
	'confluence.database': {
		icon: ({ alt }) => <DatabaseObject label={alt} />,
		iconAlt: messages.hyperlinkIconDatabaseLabel,
	},
	'confluence.embed': {
		icon: ({ alt }) => <SmartLinkIcon color={token('color.icon.accent.blue')} label={alt} />,
		iconAlt: messages.hyperlinkIconEmbedLabel,
	},
};

export const getIconFromContentType = (
	contentType: string,
	subtype?: string,
): Pick<LinkSearchListItemData, 'icon' | 'iconAlt'> => {
	if (!isLinkContentType(contentType)) {
		return {
			icon: ({ alt }: { alt: string }) => <Page16Icon label={alt} />,
			iconAlt: messages.defaultAltText,
		};
	}

	if (contentType === 'jira.issue' && fg('confluence-issue-terminology-refresh')) {
		return {
			icon: ({ alt }: { alt: string }) => <Issue16Icon label={alt} />,
			iconAlt: messages.hyperlinkIconIssueLabelIssueTermRefresh,
		};
	}

	if (contentType === 'confluence.page' && subtype === 'live') {
		return {
			icon: ({ alt }) => <PageLiveDoc16Icon label={alt} />,
			iconAlt: messages.hyperlinkIconLiveDocLabel,
		};
	}

	return mapContentTypeToIcon[contentType];
};

// Workaround to get alt text for images from url
// Can be removed when alt={iconAlt} will be available from GraphQL
// More details: https://a11y-internal.atlassian.net/browse/AK-811
export const getCorrectAltByIconUrl = (iconUrl: string): {
    id: string;
    defaultMessage: string;
    description: string;
} => {
	let alt = messages.defaultAltText;
	// TODO add in extra cases for V3 api
	if (iconUrl.includes('story.svg')) {
		alt = messages.story;
	}

	if (iconUrl.includes('epic.svg')) {
		alt = messages.epic;
	}

	if (iconUrl.includes('avatarId=10318')) {
		alt = messages.task;
	}

	if (iconUrl.includes('avatarId=10303')) {
		alt = messages.bug;
	}

	if (iconUrl.includes('avatarId=10518')) {
		alt = messages.subTask;
	}

	if (iconUrl.includes('avatarId=10310')) {
		alt = messages.improvement;
	}

	return alt;
};

export const getIconFromIconUrl = (
	iconUrl: string,
): Pick<LinkSearchListItemData, 'icon' | 'iconAlt'> => {
	return {
		icon: iconUrl,
		iconAlt: getCorrectAltByIconUrl(iconUrl),
	};
};
